import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { AddShoppingCart, LocationOn } from '@mui/icons-material';
import {
  Badge,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CallIcon from '@mui/icons-material/Call';

import { useShop } from '../../context/ShopContext'; 
// --- AUTH IMPORTS ---
import { useAuth } from '../../context/AuthContext'; 
import UserProfileDropdown from './UserProfileDropdown'; 
import Login from './Login'; 
// --- END AUTH IMPORTS ---

import Slogo from '../../assests/Slogo.png'; 

// Define a type for categories
interface Category {
  title: string;
  href: string; 
}

const categories: Category[] = [
  { title: "EARRINGS", href: "/earrings" },
  { title: "NECKLACES", href: "/necklaces" },
  { title: "BANGLES", href: "/bangles" },
  { title: "CHAINS", href: "/chains" },
  { title: "RINGS", href: "/rings" },
  { title: "PENDANTS", href: "/pendants" },
  { title: "BRACELETS", href: "/bracelets" },
  { title: "COINS & BARS", href: "/coins-bars" },
  { title: "ALL JEWELLERY", href: "/all-jewellery" },
  { title: "COLLECTIONS", href: "/collections" },
];

// Updated Component: Backend se data laakar Slider/Carousel ki tarah dikhayega
const VerticalTicker: React.FC = () => {
    const [messages, setMessages] = useState<string[]>(["⏳ Fetching Latest Rates..."]);
    const [currentIndex, setCurrentIndex] = useState(0);

    // 1. Data Fetch Karna Backend Se
    useEffect(() => {
        let isMounted = true;

        const fetchLivePrices = async () => {
            try {
                // const response = await fetch('http://localhost:8080/api/metal-prices');
                const response = await fetch('https://sardar-jewellers.com/api/metal-prices');
                
                if (response.ok) {
                    const data = await response.json();
                    
                    // 10 gram ka rate nikalna aur Indian format (commas) mein set karna
                    const price24K = (data.gold24K * 10).toLocaleString('en-IN', { maximumFractionDigits: 0 });
                    const price22K = (data.gold22K * 10).toLocaleString('en-IN', { maximumFractionDigits: 0 });
                    const price18K = (data.gold18K * 10).toLocaleString('en-IN', { maximumFractionDigits: 0 });
                    const silverKg = data.silverKg.toLocaleString('en-IN', { maximumFractionDigits: 0 });

                    if (isMounted) {
                        // Array banaya jo ek ke baad ek dikhega
                        setMessages([
                            `💰 Today's Gold Rate (10g): 24KT - ₹${price24K}`,
                            `💰 Today's Gold Rate (10g): 22KT - ₹${price22K}`,
                            `💰 Today's Gold Rate (10g): 18KT - ₹${price18K}`,
                            `⚖️ Today's Silver Rate (1kg) - ₹${silverKg}`
                        ]);
                    }
                } else {
                    if (isMounted) setMessages(["💰 Checkout our store for today's best rates!"]);
                }
            } catch (error) {
                console.error("Backend fetch error:", error);
                if (isMounted) setMessages(["💰 Checkout our store for today's best rates!"]);
            }
        };

        fetchLivePrices();

        return () => {
            isMounted = false;
        };
    }, []);

    // 2. Har 3 Second mein Message badalna (Slider Logic)
    useEffect(() => {
        if (messages.length <= 1) return; // Agar error ho ya loading ho toh slider mat chalao

        const intervalId = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % messages.length);
        }, 3000); // 3000ms = 3 seconds

        return () => clearInterval(intervalId); // Component hatega toh timer band ho jayega
    }, [messages]);

    return (
        <div className="text-center font-semibold text-yellow-300 h-8 w-full flex items-center justify-center px-2">
            <span 
                key={currentIndex} // Key change hone se React isko naya element maanta hai (animations ke liye acha hai)
                className="truncate text-xs sm:text-sm tracking-wide transition-opacity duration-500"
            >
                {messages[currentIndex]}
            </span>
        </div>
    );
};

const Navbar: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  
  const { cartItems = [] } = useShop() || {}; 
  const { user } = useAuth() || {}; 
  const isLoggedIn = !!user; 

  const googleMapsLink = "https://maps.google.com/?q=" + encodeURIComponent("Sardar Jewellers, Kalyanpur, Kanpur, UP");

  return (
    <header className='sticky top-0 z-50 w-full bg-transparent'>
      
      {/* Top Bar */}
      <div className="bg-amber-950 text-white p-2 px-4 text-xs md:text-sm flex justify-between items-center relative z-10 border-b border-amber-800">

        {/* Hidden on small screens to prioritize the ticker */}
        <div className="hidden md:flex items-center gap-2 justify-start w-1/3">
          <LocationOn sx={{ fontSize: 18, color: "gold" }} />
          <a
            href={googleMapsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline cursor-pointer flex items-center gap-1"
            aria-label="View address on Google Maps"
          >
            Address: <span className='whitespace-nowrap'>Kalyanpur, Kanpur, UP</span>
          </a>
        </div>
        
        <div className='flex justify-center w-full md:w-1/3'>
            <VerticalTicker />
        </div>

        {/* Hidden on extra small screens, formatted as a clickable tel link for SEO/Mobile */}
        <div className="hidden sm:flex items-center gap-2 justify-end whitespace-nowrap w-1/3">
          <CallIcon sx={{ fontSize: 18, color: "gold" }} />
          <a href="tel:+918441856072" className="hover:underline font-medium" aria-label="Call Contact Support">
            Contact Us: +91 8441856072
          </a>
        </div>

      </div>

      {/* Middle Bar - Logo, Search, and Action Icons */}
      <Box className='bg-white shadow-sm relative z-10'>
        <div className='flex justify-between items-center px-3 lg:px-8 h-[75px] border-b'>
          
          <div className='flex items-center gap-2'>
            {!isLarge && (
              <IconButton onClick={() => setDrawerOpen(true)} aria-label="Open mobile menu">
                <MenuIcon />
              </IconButton>
            )}
            <Link to="/" className='flex items-center gap-2 sm:gap-3 cursor-pointer' aria-label="Go to homepage"> 
              <img src={Slogo} alt='Sardar Jewellers Logo' className='w-12 h-12 sm:w-16 sm:h-16 object-contain' />
              <span className='logo text-lg sm:text-xl md:text-2xl font-bold text-primary-color'>
                Sardar Jewellers
              </span>
            </Link> 
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
             <Link to="/cart" aria-label="View shopping cart"> 
                <IconButton aria-label="shopping cart">
                    <Badge badgeContent={cartItems.length} color="primary">
                      <AddShoppingCart />
                    </Badge>
                </IconButton>
              </Link>

            {/* Login/User Section */}
            <div className="flex items-center">
                {isLoggedIn ? (
                    <UserProfileDropdown /> 
                ) : (
                   <button
                    onClick={() => setShowLogin(true)}
                    aria-label="Login to your account"
                    className="flex items-center space-x-1 
                      text-white bg-[#00927c] 
                      transition-all duration-300 
                      px-3 py-2 rounded-lg 
                      hover:bg-transparent 
                      hover:border hover:border-[#00927c] 
                      hover:text-[#00927c]"
                    >
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path></svg>
                        <span className="hidden sm:inline font-medium">Login</span>
                    </button>
                )}
            </div>
          </div>
        </div>
      </Box>

      {/* Navigation Links Bar (Desktop Only) */}
      {isLarge && (
        <nav aria-label="Main Navigation" className="flex flex-wrap justify-center gap-6 xl:gap-8 p-3 text-sm font-medium bg-white shadow-md border-b">
          {categories.map((cat, idx) => (
            <Link
              key={idx}
              to={cat.href}
              className="px-2 py-1 cursor-pointer
                         hover:text-[#8B5E3C]
                         border-b-2 border-transparent hover:border-[#8B5E3C]
                         transition-colors duration-200 ease-in-out whitespace-nowrap"
            >
              {cat.title}
            </Link>
          ))}
        </nav>
      )}

      {/* Mobile Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 280 }} role="presentation" onClick={() => setDrawerOpen(false)}>
          <List>
            <div className="p-4 flex items-center border-b mb-2 bg-gray-50">
              <img src={Slogo} alt='Sardar Jewellers Logo' className='w-12 h-12 mr-3 object-contain' />
              <span className='text-lg font-bold text-primary-color'>Sardar Jewellers</span>
            </div>
            
            {categories.map((cat, idx) => (
              <ListItem key={idx} disablePadding>
                <Link to={cat.href} className="w-full px-4 py-3 font-medium text-gray-800 hover:bg-gray-100 transition-colors">
                  {cat.title}
                </Link>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Login Modal */}
      {showLogin && <Login open={true} onClose={() => setShowLogin(false)} />}
    </header> 
  );
};

export default Navbar;